import axios from "axios";

/**
 * balanca.service
 *
 * Cliente do aplicativo local Balança, que roda no mesmo PC do
 * checkout e expõe HTTP + WebSocket em 127.0.0.1.
 *
 * - HTTP  GET  /peso   -> leitura imediata (fallback manual)
 * - WS           ws://127.0.0.1:PORT -> stream de peso em tempo real + estabilização
 *
 * O host/porta vem da config do dispositivo cadastrada no sistema (buscada pelo
 * Form.vue e repassada para cá). Defaults conservadores para desenvolvimento.
 *
 * Observação (mixed content): se o frontend admin rodar em HTTPS, chamadas a
 * ws://127.0.0.1 podem ser bloqueadas. Nesse caso, libere connect-src para
 * ws://127.0.0.1:* na CSP do frontend, ou use o fallback HTTP.
 */

const TIMEOUT_HTTP_MS = 3000;

const balancaService = {
  _ws: null,
  _host: "127.0.0.1",
  _porta: 51234,
  _accessToken: "",
  _reconnectTimer: null,
  _manualmenteFechado: false,

  // Callbacks registrados pelo consumidor (Form.vue).
  _handlers: {
    pesoTempoReal: [],
    pesoEstavel: [],
    estado: [],
    erro: [],
  },

  /**
   * Define host/porta do agente (a partir da config do dispositivo).
   */
  configurar({ host, porta, accessToken } = {}) {
    if (host) this._host = host;
    if (porta) this._porta = porta;
    if (accessToken) this._accessToken = accessToken;
  },

  /**
   * Abre a conexão WebSocket com o agente local.
   * Reconecta automaticamente em caso de queda (backoff fixo).
   */
  conectar() {
    this._manualmenteFechado = false;
    this._fecharSocket();

    try {
      const query = encodeURIComponent(this._accessToken);
      const url = `ws://${this._host}:${this._porta}?access_token=${query}`;
      this._ws = new WebSocket(url);

      const connectionTimeout = setTimeout(() => {
        if (this._ws && this._ws.readyState === WebSocket.CONNECTING) {
          this._ws.close();
          this._emit("erro", {
            mensagem: "O aplicativo Balança não respondeu.",
          });
        }
      }, TIMEOUT_HTTP_MS);

      this._ws.onopen = () => {
        clearTimeout(connectionTimeout);
        this._limparReconexao();
        this._emit("estado", { conectado: true, origem: "ws" });
      };

      this._ws.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch (_) {
          return;
        }
        switch (payload.tipo) {
          case "peso_tempo_real":
            this._emit("pesoTempoReal", payload);
            break;
          case "peso_estavel":
            this._emit("pesoEstavel", payload);
            break;
          case "estado":
            this._emit("estado", { ...payload, origem: "ws" });
            break;
          case "erro":
            this._emit("erro", { mensagem: payload.mensagem });
            break;
        }
      };

      this._ws.onerror = () => {
        this._emit("erro", {
          mensagem: "Falha de comunicação com a balança local.",
        });
      };

      this._ws.onclose = () => {
        clearTimeout(connectionTimeout);
        this._emit("estado", { conectado: false, origem: "ws" });
        if (!this._manualmenteFechado) {
          this._agendarReconexao();
        }
      };
    } catch (e) {
      this._emit("erro", {
        mensagem: "Não foi possível conectar à balança local.",
      });
      this._agendarReconexao();
    }
  },

  /**
   * Fecha a conexão e desliga a reconexão automática.
   */
  desconectar() {
    this._manualmenteFechado = true;
    this._limparReconexao();
    this._fecharSocket();
  },

  /**
   * Leitura imediata via HTTP (fallback manual / botão "Capturar peso").
   */
  async lerPeso() {
    const client = axios.create({
      baseURL: `http://${this._host}:${this._porta}`,
      timeout: TIMEOUT_HTTP_MS,
      headers: {
        Accept: "application/json",
        "X-Balanca-Token": this._accessToken,
      },
    });
    const { data } = await client.get("/peso");
    return data;
  },

  async diagnostico() {
    const client = axios.create({
      baseURL: `http://${this._host}:${this._porta}`,
      timeout: TIMEOUT_HTTP_MS,
      headers: {
        Accept: "application/json",
        "X-Balanca-Token": this._accessToken,
      },
    });
    const { data } = await client.get("/diagnostico");
    return data;
  },

  // ---- API de registro de callbacks ----
  onPesoTempoReal(cb) {
    this._handlers.pesoTempoReal.push(cb);
  },
  onPesoEstavel(cb) {
    this._handlers.pesoEstavel.push(cb);
  },
  onEstado(cb) {
    this._handlers.estado.push(cb);
  },
  onErro(cb) {
    this._handlers.erro.push(cb);
  },

  limparCallbacks() {
    this._handlers = {
      pesoTempoReal: [],
      pesoEstavel: [],
      estado: [],
      erro: [],
    };
  },

  // ---- helpers internos ----
  _emit(evento, payload) {
    (this._handlers[evento] || []).forEach((cb) => {
      try {
        cb(payload);
      } catch (_) {
        /* ignore */
      }
    });
  },

  _fecharSocket() {
    if (this._ws) {
      const ws = this._ws;
      this._ws = null;
      try {
        ws.onclose = null;
        ws.close();
      } catch (_) {
        /* ignore */
      }
    }
  },

  _agendarReconexao() {
    if (this._reconnectTimer) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (!this._manualmenteFechado) this.conectar();
    }, 4000);
  },

  _limparReconexao() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  },
};

export default balancaService;
